import type { JSX } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { VueDAigle } from '../../types.js';
import commun from './commun.module.css';
import { LienConsole, Section } from './composants.js';
import { useDroitsVue } from './droits.js';
import { useFormats } from './formats.js';
import styles from './Jalons.module.css';

const PASTILLE_ETAT: Record<string, string> = {
  atteint: commun.pastilleVert!,
  en_cours: commun.pastilleAccent!,
  a_venir: commun.pastilleGris!,
  abandonne: commun.pastilleGris!,
};

/**
 * La frise des jalons du projet, tels que les associés les voient. Un jalon
 * sans échéance dit « date à fixer » : aucune date n'est inventée.
 */
export function Jalons({ vue, numero }: { vue: VueDAigle; numero: string }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const { peut } = useDroitsVue(vue);

  return (
    <Section
      id="jalons"
      numero={numero}
      titre={t('vueDAigle.jalons.titre')}
      actions={
        peut('console:manage-milestones') ? (
          <LienConsole vers="/jalons" bouton>
            {t('vueDAigle.jalons.modifier')}
          </LienConsole>
        ) : undefined
      }
    >
      {vue.jalons.length === 0 ? (
        <p className={commun.noteDiscrete}>{t('vueDAigle.jalons.vide')}</p>
      ) : (
        <ol className={styles.frise}>
          {vue.jalons.map((jalon, rang) => {
            const classe =
              jalon.etat === 'atteint'
                ? styles.fait
                : jalon.etat === 'en_cours'
                  ? styles.enCours
                  : '';
            return (
              <li key={`${rang}-${jalon.libelle}`} className={`${styles.jalon} ${classe}`}>
                <span className={styles.noeud} aria-hidden="true">
                  {jalon.etat === 'atteint' ? <Check size={11} strokeWidth={2} /> : null}
                </span>
                <span className={styles.date}>
                  {jalon.echeanceLe ? f.date(jalon.echeanceLe) : t('vueDAigle.jalons.dateAbsente')}
                </span>
                <h3>{jalon.libelle}</h3>
                <span className={`${commun.pastille} ${PASTILLE_ETAT[jalon.etat] ?? ''}`}>
                  {t(`vueDAigle.jalons.etat.${jalon.etat}`, { defaultValue: jalon.etat })}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Section>
  );
}
